import type { Dispatch } from '@atlaskit/editor-common/event-dispatcher';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { Node as PMNode, Slice } from '@atlaskit/editor-prosemirror/model';
import type { ReadonlyTransaction, Transaction } from '@atlaskit/editor-prosemirror/state';
import { PluginKey } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import {
	buildDuplicatedMauiAppUrl,
	buildMovedUrl,
	extractPastedUrl,
	parseMauiAppLink,
	parseMauiDiagramLink,
} from './detect-maui-link';

export type MauiDiagramLinkInfo = {
	contentId: string;
	originalUrl: string;
};

export type MauiAppLinkInfo = {
	appId: string;
	originalUrl: string;
};

export type MauiDiagramPasteConfig = {
	duplicateMauiApp?: (appId: string, containerContentId: string) => Promise<{ id: string }>;
	getCurrentPageId: () => string | undefined;
	moveWhiteboardToPage: (whiteboardContentId: string, targetParentPageId: string) => Promise<void>;
	resolveContentSpaceKey: (contentId: string) => Promise<string | null>;
	targetSpaceKey: string;
};

export enum MauiDiagramPasteFlagType {
	SUCCESS = 'success',
	ERROR = 'error',
}

export type MauiDiagramPasteFlag = {
	description: string;
	id: string;
	title: string;
	type: MauiDiagramPasteFlagType;
};

export type MauiDiagramPastePluginState = {
	activeFlag: MauiDiagramPasteFlag | null;
	isMoving: boolean;
};

export enum ActionType {
	MOVE_STARTED = 'MOVE_STARTED',
	MOVE_SUCCEEDED = 'MOVE_SUCCEEDED',
	MOVE_FAILED = 'MOVE_FAILED',
	DUPLICATE_STARTED = 'DUPLICATE_STARTED',
	DUPLICATE_SUCCEEDED = 'DUPLICATE_SUCCEEDED',
	DUPLICATE_FAILED = 'DUPLICATE_FAILED',
	DISMISS_FLAG = 'DISMISS_FLAG',
}

type CardMutation = {
	newAttrs: Record<string, unknown>;
	pos: number;
};

type LinkMarkMutation = {
	from: number;
	newMark: PMNode['marks'][number];
	oldMark: PMNode['marks'][number];
	to: number;
};

type ExtensionMutation = {
	newAttrs: Record<string, unknown>;
	pos: number;
};

type TextReplaceMutation = {
	from: number;
	newUrl: string;
	to: number;
};

export const mauiDiagramPastePluginKey: PluginKey<MauiDiagramPastePluginState> =
	new PluginKey<MauiDiagramPastePluginState>('mauiDiagramPastePlugin');

const initialState: MauiDiagramPastePluginState = {
	isMoving: false,
	activeFlag: null,
};

const CARD_NODE_NAMES = new Set(['inlineCard', 'blockCard', 'embedCard']);
const ROVO_SPACE_PREFIX = '~ROVO';

/**
 * Checks whether the document already contains a reference to the given URL.
 * Only inspects card nodes and extension nodes (both flat and macroParams
 * formats) — link marks and plain text are intentionally skipped.
 *
 * Returns `true` on the first match, stopping traversal early.
 */
function docContainsUrl(doc: PMNode, url: string): boolean {
	let found = false;

	doc.descendants((node) => {
		if (found) {
			return false;
		}

		if (CARD_NODE_NAMES.has(node.type.name) && node.attrs?.url?.includes(url)) {
			found = true;
			return false;
		}

		if (node.type.name === 'extension') {
			// Flat format: parameters.url
			if (node.attrs?.parameters?.url?.includes(url)) {
				found = true;
				return false;
			}
			// Native embed macro format: parameters.macroParams.url.value
			if (node.attrs?.parameters?.macroParams?.url?.value?.includes(url)) {
				found = true;
				return false;
			}
		}

		return true;
	});

	return found;
}

/**
 * Searches the editor document for any occurrence of the original URL and
 * replaces it with the new URL. Handles:
 * - inlineCard / blockCard / embedCard nodes with a url attribute
 * - extension nodes with a parameters.url attribute (native embeds)
 * - Hyperlink marks with the URL as href
 * - Plain text nodes containing the URL
 *
 * Mutations are collected during traversal and applied in reverse document
 * order so that earlier positions are not invalidated by later mutations.
 */
function updateUrlInDoc(tr: Transaction, originalUrl: string, newUrl: string): void {
	const cardMutations: CardMutation[] = [];
	const extensionMutations: ExtensionMutation[] = [];
	const linkMarkMutations: LinkMarkMutation[] = [];
	const textReplaceMutations: TextReplaceMutation[] = [];

	tr.doc.descendants((node, pos) => {
		if (CARD_NODE_NAMES.has(node.type.name) && node.attrs?.url?.includes(originalUrl)) {
			cardMutations.push({
				pos,
				newAttrs: {
					...node.attrs,
					url: node.attrs.url.replace(originalUrl, newUrl),
				},
			});
			return false;
		}

		// Native embed extension nodes store the URL in parameters.url
		if (node.type.name === 'extension' && node.attrs?.parameters?.url?.includes(originalUrl)) {
			extensionMutations.push({
				pos,
				newAttrs: {
					...node.attrs,
					parameters: {
						...node.attrs.parameters,
						url: node.attrs.parameters.url.replace(originalUrl, newUrl),
					},
				},
			});
			return false;
		}

		// Native embed macro extension nodes store the URL in parameters.macroParams.url.value
		if (
			node.type.name === 'extension' &&
			node.attrs?.parameters?.macroParams?.url?.value?.includes(originalUrl)
		) {
			extensionMutations.push({
				pos,
				newAttrs: {
					...node.attrs,
					parameters: {
						...node.attrs.parameters,
						macroParams: {
							...node.attrs.parameters.macroParams,
							url: {
								...node.attrs.parameters.macroParams.url,
								value: node.attrs.parameters.macroParams.url.value.replace(originalUrl, newUrl),
							},
						},
					},
				},
			});
			return false;
		}

		if (node.isText) {
			node.marks?.forEach((mark) => {
				if (mark.type.name === 'link' && mark.attrs?.href?.includes(originalUrl)) {
					const newHref = mark.attrs.href.replace(originalUrl, newUrl);
					const newMark = mark.type.create({ ...mark.attrs, href: newHref });
					linkMarkMutations.push({
						from: pos,
						to: pos + node.nodeSize,
						oldMark: mark,
						newMark,
					});
				}
			});

			if (node.text?.includes(originalUrl)) {
				const startIdx = node.text.indexOf(originalUrl);
				textReplaceMutations.push({
					from: pos + startIdx,
					to: pos + startIdx + originalUrl.length,
					newUrl,
				});
				return false;
			}
		}

		return true;
	});

	// Apply card node mutations in reverse order (setNodeMarkup doesn't change positions)
	for (let i = cardMutations.length - 1; i >= 0; i--) {
		const { pos, newAttrs } = cardMutations[i];
		tr.setNodeMarkup(pos, undefined, newAttrs);
	}

	// Apply extension node mutations in reverse order
	for (let i = extensionMutations.length - 1; i >= 0; i--) {
		const { pos, newAttrs } = extensionMutations[i];
		tr.setNodeMarkup(pos, undefined, newAttrs);
	}

	// Apply link mark mutations in reverse order (removeMark/addMark don't change positions)
	for (let i = linkMarkMutations.length - 1; i >= 0; i--) {
		const { from, to, oldMark, newMark } = linkMarkMutations[i];
		tr.removeMark(from, to, oldMark);
		tr.addMark(from, to, newMark);
	}

	// Apply text replacements in reverse order (replaceWith can change text length)
	for (let i = textReplaceMutations.length - 1; i >= 0; i--) {
		const { from, to, newUrl: url } = textReplaceMutations[i];
		tr.replaceWith(from, to, tr.doc.type.schema.text(url));
	}
}

/**
 * Extracts a MAUI app URL from a pasted ProseMirror Slice by inspecting
 * extension nodes for a URL in the macroParams.url.value or parameters.url
 * structure. Returns the URL string if found, or null.
 */
function extractMauiAppUrlFromSlice(slice?: Slice): string | null {
	if (!slice?.content?.descendants) {
		return null;
	}

	let foundUrl: string | null = null;

	slice.content.descendants((node: PMNode) => {
		if (foundUrl) {
			return false;
		}

		if (node.type.name === 'extension') {
			const macroUrl = node.attrs?.parameters?.macroParams?.url?.value;
			if (macroUrl && typeof macroUrl === 'string') {
				foundUrl = macroUrl;
				return false;
			}
			const flatUrl = node.attrs?.parameters?.url;
			if (flatUrl && typeof flatUrl === 'string') {
				foundUrl = flatUrl;
				return false;
			}
		}

		return true;
	});

	return foundUrl;
}

/**
 * Kicks off the async move workflow:
 * 1. Resolves the whiteboard's actual space key
 * 2. If the space starts with ~ROVO (Rovo system space), proceeds with the move
 * 3. Otherwise, skips the move (whiteboard is already in a real space)
 *
 * On success, updates the URL in the editor document to replace the space
 * key with the target space key.
 */
function startMoveWorkflow(
	view: EditorView,
	config: MauiDiagramPasteConfig,
	linkInfo: MauiDiagramLinkInfo,
	currentPageId: string,
): void {
	config
		.resolveContentSpaceKey(linkInfo.contentId)
		.then((spaceKey) => {
			if (!spaceKey || !spaceKey.startsWith(ROVO_SPACE_PREFIX)) {
				// Not in a Rovo system space — already moved or not a MAUI diagram.
				// Update the URL in the doc if the space was resolved (so re-paste
				// from the same clipboard doesn't trigger another check).
				if (spaceKey) {
					const movedUrl = buildMovedUrl(linkInfo.originalUrl, spaceKey);
					const updateTr = view.state.tr;
					updateUrlInDoc(updateTr, linkInfo.originalUrl, movedUrl);
					view.dispatch(updateTr);
				}
				return;
			}

			const startTr = view.state.tr;
			startTr.setMeta(mauiDiagramPastePluginKey, {
				type: ActionType.MOVE_STARTED,
			});
			view.dispatch(startTr);

			return config.moveWhiteboardToPage(linkInfo.contentId, currentPageId).then(() => {
				const movedUrl = buildMovedUrl(linkInfo.originalUrl, config.targetSpaceKey);
				const successTr = view.state.tr;

				updateUrlInDoc(successTr, linkInfo.originalUrl, movedUrl);

				successTr.setMeta(mauiDiagramPastePluginKey, {
					type: ActionType.MOVE_SUCCEEDED,
				});
				view.dispatch(successTr);
			});
		})
		.catch((error: Error) => {
			const failTr = view.state.tr;
			failTr.setMeta(mauiDiagramPastePluginKey, {
				type: ActionType.MOVE_FAILED,
				errorMessage:
					error?.message ||
					"We couldn't move this diagram to your page. It won't be visible to other users until it's moved out of its temporary space. Please try again or move it manually.",
			});
			view.dispatch(failTr);
		});
}

/**
 * Kicks off the async duplicate workflow for MAUI app links:
 * 1. Calls duplicateMauiApp with the app ID and current page ID
 * 2. On success, updates all URLs in the doc to reference the new app ID
 */
function startDuplicateWorkflow(
	view: EditorView,
	config: MauiDiagramPasteConfig,
	linkInfo: MauiAppLinkInfo,
	currentPageId: string,
): void {
	if (!config.duplicateMauiApp) {
		return;
	}

	const startTr = view.state.tr;
	startTr.setMeta(mauiDiagramPastePluginKey, {
		type: ActionType.DUPLICATE_STARTED,
	});
	view.dispatch(startTr);

	config
		.duplicateMauiApp(linkInfo.appId, currentPageId)
		.then(({ id: newAppId }) => {
			const duplicatedUrl = buildDuplicatedMauiAppUrl(linkInfo.originalUrl, newAppId);
			const successTr = view.state.tr;

			updateUrlInDoc(successTr, linkInfo.originalUrl, duplicatedUrl);

			successTr.setMeta(mauiDiagramPastePluginKey, {
				type: ActionType.DUPLICATE_SUCCEEDED,
			});
			view.dispatch(successTr);
		})
		.catch((error: Error) => {
			const failTr = view.state.tr;
			failTr.setMeta(mauiDiagramPastePluginKey, {
				type: ActionType.DUPLICATE_FAILED,
				errorMessage: error?.message || "We couldn't duplicate this component. Please try again.",
			});
			view.dispatch(failTr);
		});
}

/**
 * Creates the ProseMirror plugin that handles paste detection and movement
 * of MAUI diagram whiteboards to the current page.
 *
 * The paste handler detects the MAUI link and captures it in a closure-scoped
 * queue (not in plugin state) so that no transactions are dispatched during
 * the card plugin's async URL → embedCard resolution window. The move
 * workflow is kicked off after a delay, only dispatching transactions
 * (MOVE_STARTED, MOVE_SUCCEEDED, MOVE_FAILED) once the card plugin has
 * finished resolving.
 */
export function createMauiDiagramPastePmPlugin(
	dispatch: Dispatch,
	config: MauiDiagramPasteConfig,
): SafePlugin {
	// Pending moves are tracked outside plugin state to avoid dispatching
	// transactions that would interfere with the card plugin's async
	// URL-to-embedCard resolution (which checks document structure via
	// isFreshlyPastedOnNewLine).
	let pendingMoveTimer: ReturnType<typeof setTimeout> | null = null;

	return new SafePlugin({
		key: mauiDiagramPastePluginKey,

		state: {
			init: (): MauiDiagramPastePluginState => initialState,

			apply(
				tr: ReadonlyTransaction,
				pluginState: MauiDiagramPastePluginState,
			): MauiDiagramPastePluginState {
				const meta = tr.getMeta(mauiDiagramPastePluginKey);
				if (!meta) {
					return pluginState;
				}

				let nextState: MauiDiagramPastePluginState;

				switch (meta.type) {
					case ActionType.MOVE_STARTED:
					case ActionType.DUPLICATE_STARTED:
						nextState = {
							...pluginState,
							isMoving: true,
							activeFlag: null,
						};
						break;
					case ActionType.MOVE_SUCCEEDED:
						nextState = {
							isMoving: false,
							activeFlag: {
								id: 'maui-diagram-move-success',
								type: MauiDiagramPasteFlagType.SUCCESS,
								title: 'Diagram moved',
								description: 'The diagram whiteboard has been moved to this page.',
							},
						};
						break;
					case ActionType.DUPLICATE_SUCCEEDED:
						nextState = {
							isMoving: false,
							activeFlag: null,
						};
						break;
					case ActionType.MOVE_FAILED:
						nextState = {
							isMoving: false,
							activeFlag: {
								id: 'maui-diagram-move-error',
								type: MauiDiagramPasteFlagType.ERROR,
								title: 'Could not move diagram',
								description:
									meta.errorMessage ||
									"We couldn't move this diagram to your page. It won't be visible to other users until it's moved out of its temporary space. Please try again or move it manually.",
							},
						};
						break;
					case ActionType.DUPLICATE_FAILED:
						nextState = {
							isMoving: false,
							activeFlag: {
								id: 'maui-app-duplicate-error',
								type: MauiDiagramPasteFlagType.ERROR,
								title: 'Could not duplicate component',
								description:
									meta.errorMessage || "We couldn't duplicate this component. Please try again.",
							},
						};
						break;
					case ActionType.DISMISS_FLAG:
						nextState = { ...pluginState, activeFlag: null };
						break;
					default:
						return pluginState;
				}

				dispatch(mauiDiagramPastePluginKey, nextState);
				return nextState;
			},
		},

		props: {
			handlePaste(view: EditorView, event: ClipboardEvent, slice?: Slice) {
				const { clipboardData } = event;
				if (!clipboardData) {
					return false;
				}

				const url = extractPastedUrl(clipboardData);

				const diagramLinkInfo = url ? parseMauiDiagramLink(url) : null;
				let appLinkInfo = url && !diagramLinkInfo ? parseMauiAppLink(url) : null;

				// If no MAUI link was found in the plain text, check the pasted
				// Slice for extension nodes containing a MAUI app URL (e.g. when
				// a native embed extension is copy-pasted from another page).
				if (!diagramLinkInfo && !appLinkInfo) {
					const sliceUrl = extractMauiAppUrlFromSlice(slice);
					if (sliceUrl) {
						appLinkInfo = parseMauiAppLink(sliceUrl);
					}
				}

				if (!diagramLinkInfo && !appLinkInfo) {
					return false;
				}

				const currentPageId = config.getCurrentPageId();
				if (!currentPageId) {
					// Dispatch the error on the next tick so it doesn't
					// interfere with the paste transaction pipeline
					const errorAction = diagramLinkInfo
						? ActionType.MOVE_FAILED
						: ActionType.DUPLICATE_FAILED;
					setTimeout(() => {
						const tr = view.state.tr;
						tr.setMeta(mauiDiagramPastePluginKey, {
							type: errorAction,
							errorMessage:
								'Could not determine the current page. Please save the page first and try again.',
						});
						view.dispatch(tr);
					}, 0);
					return false;
				}

				// If this is a MAUI app link that already exists on the page,
				// skip the duplicate workflow and just let the link paste normally.
				if (appLinkInfo && docContainsUrl(view.state.doc, appLinkInfo.originalUrl)) {
					return false;
				}

				// Clear any previously scheduled move — if the user pastes
				// back-to-back, only the latest paste triggers a move
				if (pendingMoveTimer !== null) {
					clearTimeout(pendingMoveTimer);
				}

				// Schedule the workflow after a delay to avoid dispatching
				// transactions during the card plugin's async URL → embedCard
				// resolution window. The card plugin's isFreshlyPastedOnNewLine
				// check is sensitive to document/selection state changes.
				pendingMoveTimer = setTimeout(() => {
					pendingMoveTimer = null;
					if (diagramLinkInfo) {
						startMoveWorkflow(view, config, diagramLinkInfo, currentPageId);
					} else if (appLinkInfo) {
						startDuplicateWorkflow(view, config, appLinkInfo, currentPageId);
					}
				}, 0);

				// Return false — let the default paste pipeline handle insertion
				// so the URL is auto-converted to a smart link as normal
				return false;
			},
		},
	});
}
